// Font transformation utilities - only transform HTML structure, preserve font names
export const fontNameToCSSVariable = (fontName: string): string => {
    // The goal is to use fonts exactly as provided by the theme system
    // We only need to ensure proper CSS variable format

    // If it's already a CSS variable, preserve as-is
    if (fontName.startsWith('var(--')) {
        return fontName;
    }

    // For all other font names, preserve them as-is
    // The theme system should handle the proper font loading
    return fontName;
};

export const colorValueToCSSVariable = (colorValue: string): string => {
    // Only convert known color values to CSS variables
    // Preserve any existing CSS variables or theme-specific colors
    if (colorValue.startsWith('var(--')) {
        return colorValue; // Already a CSS variable, preserve as-is
    }

    // For now, preserve other color values as-is
    // Could add specific color mappings here if needed
    return colorValue;
};

export const transformFontTags = (html: string): string => {
    // Only transform <font face="..."> tags to use CSS variables
    return html.replace(
        /<font\s+face="([^"]+)"([^>]*)>(.*?)<\/font>/g,
        (_, fontName, __, content) => {
            const cssVariable = fontNameToCSSVariable(fontName);
            return `<span style="font-family: ${cssVariable}">${content}</span>`;
        }
    );
};

export const transformStyleAttributes = (html: string): string => {
    // Transform inline color styles to preserve CSS variables, but keep other styles intact
    return html.replace(/style="([^"]*?)"/g, (_, styleContent) => {
        // Only transform color values, leave everything else as-is
        const transformedStyle = styleContent.replace(
            /(color|background-color|border-color):\s*([^;]+)/g,
            (__: string, property: string, value: string) => {
                const transformedValue = colorValueToCSSVariable(value.trim());
                return `${property}: ${transformedValue}`;
            }
        );
        return `style="${transformedStyle}"`;
    });
};

// PDF-specific transformations to improve media display and compatibility
export const transformForPDF = (html: string): string => {
    let transformed = html;

    // Note: Image path conversion is handled by server-side replaceRelativeUrls function
    // in documentRender.tsx - we preserve relative paths for that process

    // 2. Ensure images have proper sizing attributes for PDF
    transformed = transformed.replace(
        /<img([^>]*?)style="([^"]*?)"([^>]*?)>/g,
        (_, beforeStyle, styleContent, afterStyle) => {
            // Ensure max-width and height are set for PDF compatibility
            if (!styleContent.includes('max-width')) {
                styleContent += '; max-width: 100%';
            }
            if (!styleContent.includes('height') && !styleContent.includes('min-height')) {
                styleContent += '; height: auto';
            }
            // Add display block for better PDF rendering
            if (!styleContent.includes('display')) {
                styleContent += '; display: block';
            }
            return `<img${beforeStyle}style="${styleContent}"${afterStyle}>`;
        }
    );

    // 3. Convert font tags to proper span elements for better PDF compatibility
    transformed = transformed.replace(
        /<font\s+color="([^"]+)"([^>]*?)>(.*?)<\/font>/g,
        '<span style="color: $1">$3</span>'
    );

    // 4. Convert font size attributes to CSS
    transformed = transformed.replace(
        /<font\s+style="font-size:\s*(\d+)px;"([^>]*?)>(.*?)<\/font>/g,
        '<span style="font-size: $1px">$3</span>'
    );

    // 5. Normalize background colors for better PDF rendering
    transformed = transformed.replace(
        /background-color:\s*rgb\((\d+),\s*(\d+),\s*(\d+)\)/g,
        (_, r, g, b) => {
            // Convert RGB to hex for better PDF compatibility
            const toHex = (n: string) => parseInt(n).toString(16).padStart(2, '0');
            return `background-color: #${toHex(r)}${toHex(g)}${toHex(b)}`;
        }
    );

    // 6. Ensure proper block structure for PDF layout
    transformed = transformed.replace(
        /<div><br><\/div>/g,
        '<div style="height: 1em;">&nbsp;</div>'
    );

    // 7. Add proper line-height for better PDF text rendering
    transformed = transformed.replace(
        /<(h[1-6]|p|div)(?!\s+style="[^"]*line-height)([^>]*?)>/g,
        '<$1 style="line-height: 1.4"$2>'
    );

    return transformed;
};

export const transformResourceSpans = (html: string): string => {
    // Transform resource string spans back to template format
    return html.replace(
        /<span[^>]*data-resource-key="([^"]+)"[^>]*>[^<]*<\/span>/g,
        (_, resourceKey) => `{{ resourceStrings.${resourceKey} }}`
    );
};

export const transformResourceStringsToSpans = (
    html: string,
    resourceStrings?: Record<string, string>
): string => {
    // Transform {{ resourceStrings.key }} back to visual spans for editing
    if (!resourceStrings) return html;

    return html.replace(/\{\{\s*resourceStrings\.([^}]+)\s*\}\}/g, (_, resourceKey) => {
        const displayValue = resourceStrings[resourceKey] || resourceKey;
        return `<span data-resource-key="${resourceKey}" style="background-color: #e7f3ff; border: 1px dashed #007cba; padding: 2px 4px; border-radius: 3px; display: inline-block; margin: 0 2px;">${displayValue}</span>`;
    });
};

export const transformHtmlContent = (html: string): string => {
    // Apply all transformations: fonts, colors, and resource strings
    let transformed = transformFontTags(html);
    transformed = transformStyleAttributes(transformed);
    transformed = transformResourceSpans(transformed);
    return transformed;
};

export const transformHtmlContentForPDF = (html: string): string => {
    // Apply all transformations including PDF-specific ones
    let transformed = transformHtmlContent(html);
    transformed = transformForPDF(transformed);
    return transformed;
};
